import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  PhotoIcon,
  ArrowDownTrayIcon,
  LockClosedIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";
import { publicApi } from "../services/api";

interface Photo {
  id: string;
  url: string;
  originalName: string;
  uploadedAt: string;
}

const PhotoGalleryPage: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);

  // Check if already authenticated (simple localStorage check)
  useEffect(() => {
    const savedAuth = localStorage.getItem("gallery-auth");
    const savedPassword = localStorage.getItem("gallery-password");
    if (savedAuth === "true" && savedPassword) {
      setIsAuthenticated(true);
      setPassword(savedPassword);
    }
  }, []);

  // Fetch photos
  const {
    data: photosData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["gallery-photos", password],
    queryFn: async () => {
      const response = await publicApi.get(
        `/photos/gallery?password=${encodeURIComponent(password)}`
      );
      return response.data;
    },
    enabled: isAuthenticated,
  });

  // Keyboard navigation for modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedPhoto || !photosData?.data?.photos?.length) return;

      const photos = photosData.data.photos;
      const currentIndex = photos.findIndex(
        (p: Photo) => p.id === selectedPhoto.id
      );

      if (e.key === "ArrowLeft") {
        const prevIndex =
          currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
        setSelectedPhoto(photos[prevIndex]);
      } else if (e.key === "ArrowRight") {
        const nextIndex =
          currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
        setSelectedPhoto(photos[nextIndex]);
      } else if (e.key === "Escape") {
        setSelectedPhoto(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedPhoto, photosData]);

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.trim()) {
      setIsAuthenticated(true);
      localStorage.setItem("gallery-auth", "true");
      localStorage.setItem("gallery-password", password);
    }
  };

  const handleDownload = async (photo: Photo) => {
    setIsDownloading(photo.id);
    try {
      const response = await fetch(photo.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = photo.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download photo. Please try again.");
    } finally {
      setIsDownloading(null);
    }
  };

  const photos: Photo[] = photosData?.data?.photos || [];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md w-full space-y-8"
        >
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center">
              <LockClosedIcon className="h-8 w-8 text-blue-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-gray-900">
              Event Photo Gallery
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Enter the access password to view event photos
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handlePasswordSubmit}>
            <div>
              <label htmlFor="password" className="sr-only">
                Access Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="gallery-password-input relative block w-full px-3 py-2 pr-10 rounded-md focus:outline-none focus:z-10 sm:text-sm"
                  placeholder="Enter access password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 focus:outline-none"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <button
                type="submit"
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Access Gallery
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading photos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center">
            <XMarkIcon className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Access Denied
          </h2>
          <p className="mt-2 text-gray-600">
            Invalid password or gallery not available.
          </p>
          <button
            onClick={() => {
              setIsAuthenticated(false);
              setPassword("");
              localStorage.removeItem("gallery-auth");
              localStorage.removeItem("gallery-password");
            }}
            className="mt-4 text-blue-600 hover:text-blue-500"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-blue-50 to-blue-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Back to Home Button - Top Left */}
          <div className="mb-6 sm:mb-8">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2 text-sm font-medium text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg hover:bg-white hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1 sm:mr-2" />
              <span className="text-xs sm:text-sm">Back to Home</span>
            </button>
          </div>

          <div className="text-center mb-8 sm:mb-12">
            <div className="flex items-center justify-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
              <PhotoIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
                Event Gallery
              </h1>
            </div>
            <p className="text-gray-600 text-sm sm:text-base lg:text-lg px-4">
              View and download images from the event
            </p>
          </div>

          {photos.length > 0 ? (
            <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-4 xl:columns-5 2xl:columns-6 gap-2 sm:gap-3 space-y-2 sm:space-y-3">
              {photos.map((photo, index) => {
                // Create varied heights for masonry effect - mobile and desktop optimized
                const mobileHeights = [
                  "h-48",
                  "h-64",
                  "h-56",
                  "h-72",
                  "h-52",
                  "h-60",
                  "h-68",
                ];
                const desktopHeights = [
                  "h-56",
                  "h-72",
                  "h-64",
                  "h-80",
                  "h-60",
                  "h-68",
                  "h-76",
                  "h-52",
                  "h-84",
                ];
                const randomHeight =
                  window.innerWidth >= 1024
                    ? desktopHeights[index % desktopHeights.length]
                    : mobileHeights[index % mobileHeights.length];

                return (
                  <motion.div
                    key={photo.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    className={`group relative ${randomHeight} w-full overflow-hidden cursor-pointer rounded-lg sm:rounded-xl shadow-md hover:shadow-lg lg:hover:shadow-xl transition-all duration-300 break-inside-avoid mb-2 sm:mb-3`}
                    onClick={() => setSelectedPhoto(photo)}
                  >
                    <img
                      src={photo.url}
                      alt={photo.originalName}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />

                    {/* Desktop overlay - hidden on mobile */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-end justify-center p-3 hidden sm:flex">
                      <motion.button
                        initial={{ opacity: 0, y: 10 }}
                        whileHover={{ opacity: 1, y: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(photo);
                        }}
                        className="flex items-center space-x-2 bg-white/90 backdrop-blur-sm rounded-full px-4 py-2 shadow-lg hover:bg-white transition-colors duration-200"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 text-gray-700" />
                        <span className="text-xs font-medium text-gray-700">
                          Download
                        </span>
                      </motion.button>
                    </div>

                    {/* Mobile touch indicator */}
                    <div className="absolute top-3 right-3 sm:hidden">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(photo);
                        }}
                        className="bg-white/80 backdrop-blur-sm rounded-full p-2 shadow-lg hover:bg-white transition-colors duration-200"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 text-gray-700" />
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-12 shadow-lg border border-blue-100 max-w-md mx-auto">
                <div className="p-4 bg-blue-100 rounded-full w-20 h-20 mx-auto flex items-center justify-center mb-6">
                  <PhotoIcon className="h-12 w-12 text-blue-600" />
                </div>
                <h3 className="mt-4 text-xl font-medium text-gray-900">
                  No photos available
                </h3>
                <p className="mt-2 text-gray-600">
                  Photos will appear here once they are uploaded by the event
                  organizers.
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </div>

      {/* Photo Modal with Navigation */}
      <AnimatePresence>
        {selectedPhoto && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50"
            onClick={() => setSelectedPhoto(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-6 right-6 z-20 p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-200"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>

              {/* Navigation Arrows */}
              {photos.length > 1 && (
                <>
                  <button
                    onClick={() => {
                      const currentIndex = photos.findIndex(
                        (p) => p.id === selectedPhoto.id
                      );
                      const prevIndex =
                        currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
                      setSelectedPhoto(photos[prevIndex]);
                    }}
                    className="absolute left-6 top-1/2 transform -translate-y-1/2 z-20 p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-200"
                  >
                    <ChevronLeftIcon className="h-8 w-8" />
                  </button>

                  <button
                    onClick={() => {
                      const currentIndex = photos.findIndex(
                        (p) => p.id === selectedPhoto.id
                      );
                      const nextIndex =
                        currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
                      setSelectedPhoto(photos[nextIndex]);
                    }}
                    className="absolute right-6 top-1/2 transform -translate-y-1/2 z-20 p-3 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-75 transition-all duration-200"
                  >
                    <ChevronRightIcon className="h-8 w-8" />
                  </button>
                </>
              )}

              {/* Image */}
              <div className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
                <img
                  src={selectedPhoto.url}
                  alt={selectedPhoto.originalName}
                  className="max-w-full max-h-full object-contain"
                />

                {/* Download Button Overlay */}
                <button
                  onClick={() => handleDownload(selectedPhoto)}
                  disabled={isDownloading === selectedPhoto.id}
                  className="absolute bottom-6 right-6 p-4 bg-black bg-opacity-70 text-white rounded-full hover:bg-opacity-90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowDownTrayIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Photo Counter */}
              {photos.length > 1 && (
                <div className="absolute bottom-6 left-6 bg-black bg-opacity-70 text-white px-4 py-2 rounded-full text-sm">
                  {photos.findIndex((p) => p.id === selectedPhoto.id) + 1} /{" "}
                  {photos.length}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PhotoGalleryPage;
